import React, { SetStateAction, useEffect, useState } from "react";
import { Map as ImmutableMap } from 'immutable';
import IvrWidget from "./IvrWidget";
import { HistoricalIvr, IvrState } from "./IvrHud";
import { Delta } from "../QueueLeaderboard";
import CompactViewHeader from "../NEW/CompactViewHeader";

interface Props {
    historicalIvr: ImmutableMap<string, HistoricalIvr>;
    ivrStates: IvrState[];
    ivrDeltas: Delta[];
    tab: string;
    mode: string
    fullscreen: string | undefined
    setFullscreen: React.Dispatch<SetStateAction<string | undefined>>
    sidebar: boolean
    accessToken: string | undefined
    excludedIvrs: Array<string>
    ivrSortDirection: string | undefined;
    ivrSortField: string | undefined;
    airSortDirection: string | undefined;
    airSortField: string | undefined
}
interface WidgetMetric {
    weight: number;
    duration: number;
}
export interface IvrMetric { 
    total: WidgetMetric;
    talking: Map<string, {
        callId: string;
        timeStamp: string;
    }>;
    transferred: WidgetMetric;
    abandoned: WidgetMetric;
    other: WidgetMetric
}

const defaultMetric: IvrMetric = {
    total: { weight: 0, duration: 0},
    transferred: { weight: 0, duration: 0 },
    talking: new Map(),
    abandoned: { weight: 0, duration: 0 },
    other: { weight: 0,  duration: 0}
};

const IvrWidgetContainer = React.memo(({historicalIvr, ivrDeltas, ivrStates, tab, mode, fullscreen, setFullscreen, sidebar, accessToken, excludedIvrs, airSortDirection, airSortField, ivrSortDirection, ivrSortField}: Props) => {
    const [ivrMetrics, setIvrMetrics] = useState<Map<string, IvrMetric>>(new Map());

    useEffect(() => {
        setIvrMetrics((prevMetrics) => {
            const copyOfMetrics = new Map(prevMetrics);
            if (ivrStates) {
                ivrStates.forEach((ivrState) => {
                    if (ivrState.ivrId) {
                        const currentMetric = copyOfMetrics.get(ivrState.ivrId) || { ...defaultMetric };
                        const updatedMetric = { ...currentMetric };
    
                        // Create a new talking map to ensure immutability
                        updatedMetric.talking = new Map(currentMetric.talking);
    
                        if (ivrState.state === "TALKING") {
                            updatedMetric.talking.set(ivrState.callId, {
                                timeStamp: ivrState.time,
                                callId: ivrState.callId,
                            });
                        }
    
                        if (ivrState.state === "TRANSFERRED") {
                            updatedMetric.transferred = {
                                weight: updatedMetric.transferred.weight + 1,
                                duration: updatedMetric.transferred.duration + (new Date(ivrState.endTime).getTime() - new Date(ivrState.time).getTime())
                            };
                            updatedMetric.talking.delete(ivrState.callId);
                        }
    
                        if (ivrState.state === "ABANDONED") {
                            updatedMetric.abandoned = {
                                weight: updatedMetric.abandoned.weight + 1,
                                duration: updatedMetric.abandoned.duration + (new Date(ivrState.endTime).getTime() - new Date(ivrState.time).getTime())
                            };
                            updatedMetric.talking.delete(ivrState.callId);
                        }
    
                        if (ivrState.state && (ivrState.state !== "TRANSFERRED" && ivrState.state !== "ABANDONED" && ivrState.state !== "TALKING")) {
                            updatedMetric.other = {
                                weight: updatedMetric.other.weight + 1,
                                duration: updatedMetric.other.duration + (new Date(ivrState.endTime).getTime() - new Date(ivrState.time).getTime())
                            };
                            updatedMetric.talking.delete(ivrState.callId);
                        }
    
                        copyOfMetrics.set(ivrState.ivrId, updatedMetric);
                    }
                });
            }
            return copyOfMetrics;
        });
    }, [ivrStates]);

    return (
        <div className="ivr-widget-container">
        <div className={`${(fullscreen === 'air' || fullscreen === 'ivr') ? "fullscreen" : ""} ${mode === 'light' ? "light" : "dark"}`}>
            {(fullscreen === 'air' || fullscreen === 'ivr') && <CompactViewHeader closeFullscreen={() => setFullscreen(undefined)} mode={mode} />}

            <div className={`ivr-grid-container ${sidebar?"sidebar-open":""} ${fullscreen?"fullscreen":""}`}>
                {
                    Array.from(historicalIvr.values()).filter((ivr) => tab === "air" ? ivr.isAir : !ivr.isAir)
                    .sort((a, b) => {
                        if (tab === "air") {
                            if (airSortField === "name") {
                                if ((a.name || "") > (b.name || "")) {
                                  return airSortDirection === "asc" ? 1 : -1;
                                }
                          
                                if ((a.name || "") < (b.name || "")) {
                                  return airSortDirection === "asc" ? -1 : 1;
                                }
                               } else {
                                if ((a.extensionNumber || "") > (b.extensionNumber || "")) {
                                  return airSortDirection === "asc" ? 1 : -1;
                                }
                          
                                if ((a.extensionNumber || "") < (b.extensionNumber || "")) {
                                  return airSortDirection === "asc" ? -1 : 1;
                                }
                               }
                                return 0;
                        }
                        if (tab === 'ivr') {
                            if (ivrSortField === "name") {
                                if ((a.name || "") > (b.name || "")) {
                                  return ivrSortDirection === "asc" ? 1 : -1;
                                }
                          
                                if ((a.name || "") < (b.name || "")) {
                                  return ivrSortDirection === "asc" ? -1 : 1;
                                }
                               }
                               if (ivrSortField === "extensionNumber") {
                                if ((a.extensionNumber || "") > (b.extensionNumber || "")) {
                                  return ivrSortDirection === "asc" ? 1 : -1;
                                }
                          
                                if ((a.extensionNumber || "") < (b.extensionNumber || "")) {
                                  return ivrSortDirection === "asc" ? -1 : 1;
                                }
                               }
                                return 0;
                        }
                        return 0;
                    })
                    .filter((ivr) => !excludedIvrs.find((i) => i === ivr.id)).map((ivr) => (
                        <IvrWidget 
                        key={ivr.id} 
                        ivr={ivr} 
                        accessToken={accessToken} 
                        mode={mode} 
                        ivrMetric={ivrMetrics.get(ivr.id) || defaultMetric}
                        delta={ivrDeltas.find((delta) => delta.userId === ivr.id)} />
                    ))
                }
            </div>
            </div>
        </div>
    );
});

export default IvrWidgetContainer;